/**
 * Hook to monitor document expirations and send push notifications
 * 
 * Checks documents and sends notifications when they're expiring soon or expired
 * Notifications are sent once per document per status to avoid spam
 * Now uses persistent dedupe via notificationGate to survive app restarts
 */

import { useEffect, useRef } from 'react';
import { subscribeToUserDocuments, type Document } from '@/services/documentVault';
import { sendDocumentExpirationNotification } from '@/services/notifications';
import {
  getDocumentExpirationDate,
  getExpirationStatus,
  getExpiredDocuments,
  getDocumentsExpiringWithin,
} from '@/utils/documentExpiration';
import { sendGatedNotification, DEFAULT_COOLDOWNS } from '@/services/notificationGate';
import { buildDedupeKey } from '@/services/notificationDedupe';

/**
 * Hook to monitor document expirations and send notifications
 */
export function useDocumentExpirationNotifications(userId: string | null) {
  const lastCheckRef = useRef<Date | null>(null);

  useEffect(() => {
    if (!userId) return;

    const checkDocuments = async (documents: Document[]) => {
      const now = new Date();
      
      // Only check once per day (to avoid spam)
      if (lastCheckRef.current) {
        const hoursSinceLastCheck = (now.getTime() - lastCheckRef.current.getTime()) / (1000 * 60 * 60);
        if (hoursSinceLastCheck < 24) {
          return;
        }
      }

      lastCheckRef.current = now;

      // Get expired documents
      const expired = getExpiredDocuments(documents);
      
      // Get documents expiring within 30 days (critical)
      const expiringVerySoon = getDocumentsExpiringWithin(documents, 30);
      
      // Get documents expiring within 90 days (warning)
      const expiringSoon = getDocumentsExpiringWithin(documents, 90);

      // Send notifications for expired documents (once per document)
      for (const doc of expired) {
        const expirationDate = getDocumentExpirationDate(doc);
        const status = getExpirationStatus(expirationDate);
        
        if (status) {
          // Use gated notification with persistent dedupe
          await sendGatedNotification(
            {
              category: 'documents',
              channel: 'push',
              priority: 'high',
              dedupeKey: buildDedupeKey.documentExpiration(doc.id, 'expired'),
              cooldownMs: DEFAULT_COOLDOWNS.documentExpiration,
            },
            () => sendDocumentExpirationNotification(
              doc.name,
              doc.type,
              status.daysUntil,
              true,
              'high'
            )
          ).catch(console.error);
        }
      }

      // Send notifications for documents expiring very soon (30 days) - once when it hits 30 days
      for (const doc of expiringVerySoon) {
        const expirationDate = getDocumentExpirationDate(doc);
        const status = getExpirationStatus(expirationDate);
        
        // Only notify when it's exactly 30 days or less (to avoid duplicate notifications)
        if (status && status.daysUntil <= 30 && status.daysUntil > 0) {
          // Use gated notification with persistent dedupe
          await sendGatedNotification(
            {
              category: 'documents',
              channel: 'push',
              priority: 'high',
              dedupeKey: buildDedupeKey.documentExpiration(doc.id, '30'),
              cooldownMs: DEFAULT_COOLDOWNS.documentExpiration,
            },
            () => sendDocumentExpirationNotification(
              doc.name,
              doc.type,
              status.daysUntil,
              false,
              'high'
            )
          ).catch(console.error);
        }
      }

      // Send notifications for documents expiring soon (90 days) - once when it hits 90 days
      for (const doc of expiringSoon) {
        const expirationDate = getDocumentExpirationDate(doc);
        const status = getExpirationStatus(expirationDate);
        
        // Only notify when it's between 60-90 days (to avoid duplicate with 30-day notification)
        if (status && status.daysUntil > 30 && status.daysUntil <= 90) {
          // Use gated notification with persistent dedupe
          await sendGatedNotification(
            {
              category: 'documents',
              channel: 'push',
              priority: 'medium',
              dedupeKey: buildDedupeKey.documentExpiration(doc.id, '90'),
              cooldownMs: DEFAULT_COOLDOWNS.documentExpiration,
            },
            () => sendDocumentExpirationNotification(
              doc.name,
              doc.type,
              status.daysUntil,
              false,
              'medium'
            )
          ).catch(console.error);
        }
      }
    };

    // Subscribe to documents
    const unsubscribe = subscribeToUserDocuments(userId, checkDocuments);

    return () => {
      unsubscribe();
    };
  }, [userId]);
}
